import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import useSchoolStore from '../../store/schoolStore';
import useLessonPlanStore from '../../store/lessonPlanStore';
import { getClassName } from '../../utils/schoolUtils';
import Button from '../../components/common/Button';
import { LuNotebookPen, LuRefreshCw } from 'react-icons/lu';
import PageHeader from '../../components/common/PageHeader';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import ErrorWidget from '../../components/common/ErrorWidget';
import LoadingMessage from '../../components/common/LoadingMessage';
import { Field, SearchInput, Select } from '../../components/common/FormControls';

// A thin bar showing a percentage score
const ScoreBar = ({ percentage, className = '' }) => (
    <div className={`h-1.5 w-full overflow-hidden rounded-full bg-ui-muted ${className}`} aria-hidden>
        <div className="h-full rounded-full bg-brand-500" style={{ width: `${Math.min(percentage, 100)}%` }} />
    </div>
);

const LessonPlanReportPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const routeParams = useParams();

    // Prioritize route parameter schoolId, fallback to query param or store
    const { schoolId: storeSchoolId, classes, getClasses, isClassesLoading } = useSchoolStore();
    const schoolId = routeParams.schoolId || searchParams.get('schoolId') || storeSchoolId;

    const {
        subjects,
        isSubjectsLoading,
        fetchSubjects,
        selectedClass,
        selectedSubject,
        selectedSubjectId,
        selectedChapterId,
        setSelectedClass,
        setSelectedSubject,
        setSelectedChapterId,
        report,
        isReportLoading,
        reportError,
        fetchLessonPlanReport
    } = useLessonPlanStore();

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'EXCELLENT' | 'GOOD' | 'AVERAGE' | 'NEEDS_ATTENTION' | 'UNTESTED'

    // Initial load: Fetch classes
    useEffect(() => {
        if (schoolId) {
            getClasses();
        }
    }, [schoolId, getClasses]);

    // When selectedClass is available, fetch subjects for that class
    useEffect(() => {
        if (schoolId && selectedClass) {
            fetchSubjects(schoolId, selectedClass);
        }
    }, [schoolId, selectedClass, fetchSubjects]);

    // Read URL params on initial mount if provided
    useEffect(() => {
        const urlClass = searchParams.get('classId') || searchParams.get('class');
        const urlSubject = searchParams.get('subject') || searchParams.get('subjectName');
        const urlSubjectId = searchParams.get('subjectId');

        if (urlClass && urlClass !== String(selectedClass)) {
            setSelectedClass(urlClass, schoolId);
        }
        if (urlSubject && urlSubject !== selectedSubject) {
            setSelectedSubject(urlSubject, urlSubjectId || null, schoolId);
        }
    }, [searchParams, schoolId, selectedClass, selectedSubject, setSelectedClass, setSelectedSubject]);

    // Handle Class Dropdown Change
    const handleClassChange = (e) => {
        const value = e.target.value;
        setSelectedClass(value, schoolId);

        // Update URL query params - remove previous subject selection on class change
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set('classId', value);
        } else {
            newParams.delete('classId');
        }
        newParams.delete('subject');
        newParams.delete('subjectId');
        setSearchParams(newParams);
    };

    // Handle Subject Dropdown Change
    const handleSubjectChange = (e) => {
        const value = e.target.value;
        const selectedSubjObj = subjects.find(subjectItem => (subjectItem.name || subjectItem.subject) === value);
        const subjId = selectedSubjObj ? (selectedSubjObj.id || null) : null;
        
        setSelectedSubject(value, subjId, schoolId);

        // Update URL query params
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set('subject', value);
            if (subjId) newParams.set('subjectId', subjId);
        } else {
            newParams.delete('subject');
            newParams.delete('subjectId');
        }
        setSearchParams(newParams);
    };

    // Manual Refresh
    const handleRefresh = () => {
        if (schoolId && selectedClass && (selectedSubject || selectedSubjectId)) {
            fetchLessonPlanReport({
                schoolId,
                classId: selectedClass,
                subject: selectedSubject,
                subjectId: selectedSubjectId
            });
        }
    };

    // Filtered lesson plans list based on search and status filter
    const filteredLessonPlans = useMemo(() => {
        if (!report || !report.lessonPlans) return [];

        return report.lessonPlans.filter(lp => {
            const matchesSearch = 
                (lp.chapterName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                lp.learningOutcomes.some(lo => 
                    (lo.topic || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (lo.learningOutcome || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (lo.competency || '').toLowerCase().includes(searchQuery.toLowerCase())
                );

            const matchesStatus = 
                statusFilter === 'ALL' || 
                lp.performanceLevel === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [report, searchQuery, statusFilter]);

    // Active selected chapter
    const activeChapter = useMemo(() => {
        if (!report || !report.lessonPlans || report.lessonPlans.length === 0) return null;
        if (!selectedChapterId) return report.lessonPlans[0];
        return report.lessonPlans.find(lp => lp.lessonPlanId === selectedChapterId) || report.lessonPlans[0];
    }, [report, selectedChapterId]);

    // Aggregated Metrics
    const metrics = useMemo(() => {
        if (!report) return null;
        return report.aggregatedMetrics;
    }, [report]);

    // Label and badge colour for a score; no evaluations means the chapter was not tested yet
    const getScoreBadge = (percentage, evaluations = 1) => {
        if (evaluations === 0) {
            return { label: 'Not tested', tone: 'grey' };
        }
        if (percentage >= 80) {
            return { label: 'Outstanding', tone: 'green' };
        }
        if (percentage >= 60) {
            return { label: 'Proficient', tone: 'blue' };
        }
        if (percentage >= 40) {
            return { label: 'Developing', tone: 'amber' };
        }
        return { label: 'Needs focus', tone: 'red' };
    };

    const renderMetrics = () => {
        const allOutcomes = report.lessonPlans.flatMap(lessonPlan => lessonPlan.learningOutcomes);
        const overallBadge = getScoreBadge(metrics.overallPercentage, metrics.totalEvaluations);
        const tiles = [
            {
                label: 'Overall mastery',
                value: `${metrics.overallPercentage}%`,
                badge: overallBadge,
                caption: `${metrics.totalSecuredMarks} of ${metrics.totalMaximumMarks} marks`,
                percentage: metrics.overallPercentage,
            },
            {
                label: 'Chapters',
                value: metrics.totalChapters,
                caption: `${report.lessonPlans.filter(lessonPlan => lessonPlan.totalEvaluations > 0).length} tested · ${report.lessonPlans.filter(lessonPlan => lessonPlan.totalEvaluations === 0).length} not tested`,
            },
            {
                label: 'Exam questions',
                value: metrics.totalQuestions,
                caption: `${metrics.totalEvaluations} marks entered`,
            },
            {
                label: 'Learning outcomes',
                value: metrics.totalOutcomes,
                caption: `${allOutcomes.filter(outcome => outcome.percentage >= 75).length} mastered · ${allOutcomes.filter(outcome => outcome.percentage < 75 && outcome.totalEvaluations > 0).length} need focus`,
            },
        ];

        return (
            <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {tiles.map((tile) => (
                    <div key={tile.label} className="rounded-xl border border-ui-line bg-ui-card px-5 py-4 shadow-card">
                        <p className="text-sm font-medium text-ui-ink-2">{tile.label}</p>
                        <div className="mt-0.5 flex items-baseline gap-2">
                            <span className="text-[28px] leading-9 font-semibold tracking-tight text-ui-ink tabular-nums">{tile.value}</span>
                            {tile.badge && <Badge tone={tile.badge.tone}>{tile.badge.label}</Badge>}
                        </div>
                        {tile.percentage !== undefined && <ScoreBar percentage={tile.percentage} className="mt-2" />}
                        <p className="mt-1.5 text-xs text-ui-ink-3">{tile.caption}</p>
                    </div>
                ))}
            </div>
        );
    };

    const renderChapterList = () => (
        <Card
            noPadding
            title={`Chapters (${filteredLessonPlans.length})`}
            className="lg:col-span-4"
        >
            <div className="flex flex-col gap-2 border-b border-ui-line-soft p-3">
                <SearchInput
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search chapter, topic or outcome"
                />
                <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Show chapters">
                    <option value="ALL">All chapters</option>
                    <option value="EXCELLENT">Outstanding (80% and above)</option>
                    <option value="GOOD">Proficient (60–79%)</option>
                    <option value="AVERAGE">Developing (40–59%)</option>
                    <option value="NEEDS_ATTENTION">Needs focus (below 40%)</option>
                    <option value="UNTESTED">Not tested</option>
                </Select>
            </div>

            <div className="ui-scroll max-h-[calc(100vh-320px)] overflow-y-auto p-2">
                {filteredLessonPlans.length === 0 ? (
                    <p className="px-3 py-8 text-center text-sm text-ui-ink-3">No chapters match these filters.</p>
                ) : (
                    filteredLessonPlans.map((chapter) => {
                        const isSelected = activeChapter && activeChapter.lessonPlanId === chapter.lessonPlanId;
                        const isTested = chapter.totalEvaluations > 0;

                        return (
                            <button
                                key={chapter.lessonPlanId}
                                type="button"
                                onClick={() => setSelectedChapterId(chapter.lessonPlanId)}
                                aria-current={isSelected ? 'true' : undefined}
                                className={`relative mb-1 w-full rounded-lg px-3 py-2.5 text-left transition-colors ${
                                    isSelected ? 'bg-ui-brand-soft' : 'hover:bg-ui-hover'
                                }`}
                            >
                                {isSelected && <span aria-hidden className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full bg-brand-500" />}
                                <div className="flex items-start justify-between gap-2">
                                    <span className="line-clamp-2 text-sm font-medium text-ui-ink">{chapter.chapterName}</span>
                                    <span className={`shrink-0 text-sm font-semibold tabular-nums ${isTested ? 'text-ui-ink' : 'text-ui-ink-3'}`}>
                                        {isTested ? `${chapter.overallPercentage}%` : '—'}
                                    </span>
                                </div>
                                <p className="mt-0.5 text-xs text-ui-ink-3">
                                    {chapter.learningOutcomes.length} outcomes · {chapter.totalQuestions} questions
                                </p>
                                {isTested && <ScoreBar percentage={chapter.overallPercentage} className="mt-2" />}
                            </button>
                        );
                    })
                )}
            </div>
        </Card>
    );

    const renderChapterDetails = () => {
        if (!activeChapter) {
            return (
                <Card className="lg:col-span-8">
                    <EmptyState title="Choose a chapter" message="Pick a chapter on the left to see its learning outcomes." />
                </Card>
            );
        }

        const chapterBadge = getScoreBadge(activeChapter.overallPercentage, activeChapter.totalEvaluations);

        return (
            <Card className="lg:col-span-8" noPadding>
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-ui-line-soft px-5 py-4">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-ui-ink">{activeChapter.chapterName}</h2>
                        <p className="mt-0.5 text-sm text-ui-ink-2">
                            {activeChapter.learningOutcomes.length} learning outcomes · {activeChapter.totalQuestions} questions marked
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <p className="text-xs text-ui-ink-3">Chapter score</p>
                            <p className="text-xl font-semibold text-ui-ink tabular-nums">
                                {activeChapter.totalEvaluations > 0 ? `${activeChapter.overallPercentage}%` : '—'}
                            </p>
                        </div>
                        <Badge tone={chapterBadge.tone}>{chapterBadge.label}</Badge>
                    </div>
                </div>

                <div className="p-5">
                    <h3 className="mb-3 text-[11px] font-semibold tracking-wider text-ui-ink-3 uppercase">
                        Learning outcomes ({activeChapter.learningOutcomes.length})
                    </h3>

                    {activeChapter.learningOutcomes.length === 0 ? (
                        <p className="rounded-lg bg-ui-muted px-4 py-8 text-center text-sm text-ui-ink-3">
                            No learning outcomes are recorded for this chapter.
                        </p>
                    ) : (
                        <ul className="flex flex-col gap-3">
                            {activeChapter.learningOutcomes.map((outcome, outcomeIndex) => {
                                const outcomeBadge = getScoreBadge(outcome.percentage, outcome.totalEvaluations);
                                const notes = [
                                    { label: 'Competency', value: outcome.competency },
                                    { label: 'Activities', value: outcome.learningActivities },
                                    { label: 'Assessment', value: outcome.assessment },
                                ].filter((note) => note.value);

                                return (
                                    <li key={outcome.id || outcomeIndex} className="rounded-lg border border-ui-line p-4">
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                {outcome.topic && <p className="text-xs font-medium text-ui-link">{outcome.topic}</p>}
                                                <h4 className="mt-0.5 text-sm font-medium text-ui-ink">
                                                    {outcome.learningOutcome || 'General learning objective'}
                                                </h4>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-2">
                                                <div className="text-right">
                                                    <p className="text-sm font-semibold text-ui-ink tabular-nums">
                                                        {outcome.totalEvaluations > 0 ? `${outcome.percentage}%` : '—'}
                                                    </p>
                                                    {outcome.maximumMarks > 0 && (
                                                        <p className="text-[11px] text-ui-ink-3 tabular-nums">
                                                            {outcome.securedMarks}/{outcome.maximumMarks} marks
                                                        </p>
                                                    )}
                                                </div>
                                                <Badge tone={outcomeBadge.tone}>{outcomeBadge.label}</Badge>
                                            </div>
                                        </div>

                                        {outcome.totalEvaluations > 0 && <ScoreBar percentage={outcome.percentage} className="mt-3" />}

                                        {notes.length > 0 && (
                                            <dl className="mt-3 grid grid-cols-1 gap-2 border-t border-ui-line-soft pt-3 text-xs sm:grid-cols-3">
                                                {notes.map((note) => (
                                                    <div key={note.label}>
                                                        <dt className="font-medium text-ui-ink-2">{note.label}</dt>
                                                        <dd className="mt-0.5 text-ui-ink-3">{note.value}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        )}

                                        <p className="mt-3 text-[11px] text-ui-ink-3">
                                            {outcome.questionsCount} linked questions · {outcome.totalEvaluations} marks entered
                                        </p>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </Card>
        );
    };

    const renderReportArea = () => {
        if (isReportLoading) {
            return (
                <Card>
                    <LoadingMessage message={`Preparing the ${selectedSubject} report for class ${getClassName(schoolId, selectedClass)}…`} />
                </Card>
            );
        }

        if (reportError) {
            return (
                <ErrorWidget error={{ message: reportError }} title="Could not load the subject report" onRetry={handleRefresh} />
            );
        }

        if (!selectedClass || !selectedSubject) {
            return (
                <Card>
                    <EmptyState
                        icon={<LuNotebookPen />}
                        title="Choose a class and subject"
                        message="Pick a class and a subject above to see how each chapter and learning outcome is going."
                    />
                </Card>
            );
        }

        if (!report) {
            return null;
        }

        return (
            <>
                {metrics && renderMetrics()}
                <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-12">
                    {renderChapterList()}
                    {renderChapterDetails()}
                </div>
            </>
        );
    };

    const page = (
        <div className="mx-auto max-w-7xl">
            <PageHeader
                title="Subject reports"
                subtitle="Chapter-wise performance, lesson plan progress and learning outcomes of a subject."
            />

            <Card className="mb-5">
                <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
                    <Field label="Class">
                        <Select id="class-select" value={selectedClass} onChange={handleClassChange} disabled={isClassesLoading}>
                            <option value="" disabled>{isClassesLoading ? 'Loading classes…' : 'Select class'}</option>
                            {classes && classes.map((classItem, classIndex) => (
                                <option key={classIndex} value={classItem.Class}>
                                    {getClassName(schoolId, classItem.Class)}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Subject">
                        <Select id="subject-select" value={selectedSubject} onChange={handleSubjectChange} disabled={!selectedClass || isSubjectsLoading}>
                            <option value="" disabled>
                                {!selectedClass
                                    ? 'Select class first'
                                    : (isSubjectsLoading
                                        ? 'Loading subjects…'
                                        : (subjects.length === 0 ? 'No subjects found' : 'Select subject'))}
                            </option>
                            {subjects && subjects.map((subjectItem, subjectIndex) => {
                                const subjectName = subjectItem.name || subjectItem.subject;
                                return (
                                    <option key={subjectIndex} value={subjectName}>
                                        {subjectName}
                                    </option>
                                );
                            })}
                        </Select>
                    </Field>
                    <Button
                        variant="secondary"
                        onClick={handleRefresh}
                        disabled={!selectedClass || !selectedSubject}
                        loading={isReportLoading}
                        leftIcon={<LuRefreshCw className="size-4" />}
                    >
                        Refresh
                    </Button>
                </div>
            </Card>

            {renderReportArea()}
        </div>
    );

    // Inside the dashboard the shell gives the page its padding; the public /lesson-plan-report link needs its own
    if (routeParams.schoolId) {
        return page;
    }

    return <div className="min-h-screen bg-ui-page px-8 py-6 text-ui-ink">{page}</div>;
};

export default LessonPlanReportPage;
